"use client";

/**
 * AttributeValueValueEditor - kind-aware editor for AttributeValue.value
 *
 * `AttributeValue.value` is typed `Any` in the backend because the actual
 * Python type (number / string / bool / list) depends on the linked
 * `Attribute`'s class+shape — info that lives outside the AV itself.
 *
 * The generic `EditableField` treats `value` as a text input and submits
 * whatever the user typed verbatim. For a `NumericAttribute(SCALAR)` slot
 * the backend correctly rejects the string with 400; the UX is rough.
 *
 * This editor inspects `av.attribute` (already loaded by the AV byid
 * query) and dispatches:
 *
 *   - NumericAttribute(SCALAR) → number input, coerce to Number on save
 *   - DiscreteAttribute(BOOLEAN) → boolean toggle
 *   - DiscreteAttribute(ENUM) → dropdown from `enum_values`
 *   - DiscreteAttribute(STRING) → text input
 *   - Anything else (NumericAttribute RANGE / VECTOR; no attribute loaded
 *     yet) → fall back to the generic text editor
 *
 * Mirrors the `enumInfo.isNumeric` coercion pattern in `EditableField.tsx`.
 *
 * The RANGE / VECTOR shapes are stubbed as text-fallback for now (they
 * need richer multi-input editors). Add them when the use case lands.
 */

import React, { useEffect, useRef, useState } from "react";
import type { Editable } from "@sourcepilot/ui/framework/data/hooks/use-editable";
import type { AttributeValue } from "@sourcepilot/generated/rflp/api/attributevalue";
import { EditableInput } from "@sourcepilot/ui/framework/ui/molecules/EditableInput";
import { Input } from "@sourcepilot/ui/framework/ui/atoms/input";
import { Button } from "@sourcepilot/ui/framework/ui/atoms/button";
import { Switch } from "@sourcepilot/ui/framework/ui/atoms/switch";
import { Edit2 } from "lucide-react";
import { NumericShape, DiscreteKind } from '@sourcepilot/generated/rflp/enums';
import { graphStore } from "@sourcepilot/framework/graph-store";
import { useReactiveNode } from "@sourcepilot/ui/framework/data/hooks/use-reactive-node";
import { versioningClient } from "@sourcepilot/ui/framework/data/versioning-client";
import { useActiveBranch } from "@sourcepilot/ui/framework/data/active-branch";
import { CalculationStateView } from "./calculation-state-view";
import { usePendingCalculation } from "@/lib/rflp/derived-pending";
import { usePinnedIsland } from "@sourcepilot/ui/framework/data/pins/pinned-islands";
import { NOTED_WIRE } from "@sourcepilot/ui/framework/data/pins/noted-conflicts";

// Mirror EditableInput's hover-highlight chrome so the value field
// looks identical to its neighbours when idle.
const HOVER_HIGHLIGHT_CLASS =
  "border border-transparent hover:bg-gray-50 hover:border-gray-300 rounded px-2 py-1";

/** Display formatting for numeric values: strip IEEE float noise
 *  (`112.81500000000001` → `112.815`) without losing magnitude.
 *  6 significant digits, re-parsed so trailing zeros drop. Display
 *  ONLY — edit drafts keep the raw value. */
export function formatMetricValue(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value);
    return String(parseFloat(value.toPrecision(6)));
  }
  return String(value);
}

interface Props {
  editable: Editable<AttributeValue> | null;
  node: AttributeValue | null;
}

/**
 * Resolve the AV's linked Attribute id from the GraphStore relationship
 * index. The AV's own `_attribute` eager-load slot is empty when the
 * relationship was registered via a DAG patch rather than the byid
 * query's eager load — so reading `node.attribute.node` would return the
 * RelationshipManager itself (its fallback) instead of the Attribute.
 *
 * Read fresh on every render. The GraphStore doesn't expose a reactive
 * relationship atom; subscribing to the AV node via the parent's
 * `useReactiveNode` causes us to re-render when DAG patches update the
 * AV, at which point any newly-arrived `attribute` edge becomes visible.
 */
function getLinkedAttributeId(node: AttributeValue | null): string | null {
  const avId = (node as any)?.id;
  if (!avId) return null;
  const targets = graphStore.getTargetNodes<any>(avId, "attribute");
  return (targets[0] as any)?.id ?? null;
}

export function AttributeValueValueEditor({ editable, node }: Props) {
  const attributeId = getLinkedAttributeId(node);
  const attribute = useReactiveNode<any>(attributeId);
  const label = (attribute as any)?._label as string | undefined;

  // An AttributeValue is always AUTHORED (item 12 set 1): a computed
  // value is a DerivedValue, shown by `DerivedValueCell` below.

  // No attribute loaded → fall through to plain text editor.
  if (!editable || !label) {
    return (
      <EditableInput
        editable={editable as any}
        field={"value" as any as never}
        placeholder="Enter value..."
        hoverHighlight
      />
    );
  }

  // --- NumericAttribute ---
  if (label === "NumericAttribute") {
    const shape = (attribute as any).shape as NumericShape | undefined;
    const isInteger = !!(attribute as any).is_integer;
    // Plausibility bounds are soft "sanity check" limits, not hard
    // validation — wiring them through as HTML5 min/max gives the
    // browser native arrow-key clamping and basic visual warnings
    // without making submission impossible. Hard bounds belong on a
    // Constraint.
    const minPlausible = (attribute as any).min_plausible ?? null;
    const maxPlausible = (attribute as any).max_plausible ?? null;

    if (shape === NumericShape.SCALAR) {
      return (
        <NumericScalarEditor
          editable={editable}
          isInteger={isInteger}
          unitSymbol={attributeValueUnitSymbol((node as any)?.id ?? null, attributeId)}
          min={minPlausible}
          max={maxPlausible}
        />
      );
    }
    // RANGE / VECTOR: fall through to text fallback for now.
  }

  // --- DiscreteAttribute ---
  if (label === "DiscreteAttribute") {
    const kind = (attribute as any).kind as DiscreteKind | undefined;

    if (kind === DiscreteKind.BOOLEAN) {
      return <BooleanEditor editable={editable} />;
    }
    if (kind === DiscreteKind.ENUM) {
      const enumValues = ((attribute as any).enum_values || []) as string[];
      const allowOther = !!(attribute as any).allow_other;
      return (
        <EnumEditor
          editable={editable}
          options={enumValues}
          allowOther={allowOther}
        />
      );
    }
    // STRING falls through to plain text editor (default behaviour fine).
  }

  // Default: plain text editor.
  return (
    <EditableInput
      editable={editable as any}
      field={"value" as any as never}
      placeholder="Enter value..."
      hoverHighlight
    />
  );
}

// --------------------------------------------------------------------------
// NumericAttribute(SCALAR) editor
// --------------------------------------------------------------------------

function NumericScalarEditor({
  editable,
  isInteger,
  unitSymbol,
  min,
  max,
}: {
  editable: Editable<AttributeValue>;
  isInteger: boolean;
  unitSymbol: string | null;
  min: number | null;
  max: number | null;
}) {
  const currentValue = (editable as any).value;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    currentValue == null ? "" : String(currentValue),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-sync draft when the underlying editable's value changes externally
  // (e.g. orchestrator wrote a new computed value).
  useEffect(() => {
    setDraft(currentValue == null ? "" : String(currentValue));
  }, [currentValue]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      (editable as any).value = null;
      await editable.save();
      setIsEditing(false);
      return;
    }
    const parsed = isInteger ? parseInt(trimmed, 10) : parseFloat(trimmed);
    if (Number.isNaN(parsed)) {
      setDraft(currentValue == null ? "" : String(currentValue));
      setIsEditing(false);
      return;
    }
    // Coerce to number BEFORE save — backend's
    // `_validate_against_attribute` rejects strings on
    // NumericAttribute(SCALAR) with a 400.
    (editable as any).value = parsed;
    try {
      await editable.save();
    } finally {
      setIsEditing(false);
    }
  };

  const cancel = () => {
    setDraft(currentValue == null ? "" : String(currentValue));
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="inline-flex items-center gap-2">
        <Input
          ref={inputRef}
          type="number"
          step={isInteger ? 1 : "any"}
          min={min ?? undefined}
          max={max ?? undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          className="min-w-0"
        />
        {unitSymbol && (
          <span className="text-xs text-gray-500">{unitSymbol}</span>
        )}
      </div>
    );
  }

  const display =
    currentValue == null ? (
      <span className="text-gray-400 italic">Enter value...</span>
    ) : (
      <>
        {formatMetricValue(currentValue)}
        {unitSymbol && (
          <span className="ml-1 text-xs text-gray-500">{unitSymbol}</span>
        )}
      </>
    );

  return (
    <div
      className={`group inline-flex items-center gap-1 ${HOVER_HIGHLIGHT_CLASS}`}
      style={{ minWidth: "100px" }}
    >
      <span className="flex-1 min-w-0">{display}</span>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsEditing(true);
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 flex-shrink-0"
      >
        <Edit2 className="h-3 w-3" style={{ color: "#00ccaa" }} />
      </Button>
    </div>
  );
}

// --------------------------------------------------------------------------
// DiscreteAttribute(BOOLEAN) editor
// --------------------------------------------------------------------------

function BooleanEditor({ editable }: { editable: Editable<AttributeValue> }) {
  const currentValue = (editable as any).value;
  const isTrue = currentValue === true;

  const toggle = async (next: boolean) => {
    (editable as any).value = next;
    await editable.save();
  };

  return (
    <div className="inline-flex items-center gap-2">
      <Switch checked={isTrue} onCheckedChange={toggle} />
      <span className="text-xs text-gray-500">
        {currentValue == null ? "—" : isTrue ? "true" : "false"}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// DiscreteAttribute(ENUM) editor
// --------------------------------------------------------------------------

function EnumEditor({
  editable,
  options,
  allowOther,
}: {
  editable: Editable<AttributeValue>;
  options: string[];
  allowOther: boolean;
}) {
  const currentValue = (editable as any).value;
  const currentStr = currentValue == null ? "" : String(currentValue);

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    (editable as any).value = v === "" ? null : v;
    await editable.save();
  };

  // When allow_other is False and the current value isn't in options,
  // show it as a disabled item so the user sees what's stored. When
  // allow_other is True, fall back to a text input.
  if (
    allowOther &&
    currentStr &&
    !options.includes(currentStr)
  ) {
    // Free-form for allow_other when value isn't in the canonical list.
    return (
      <EditableInput
        editable={editable as any}
        field={"value" as any as never}
        placeholder="Enter value..."
        hoverHighlight
      />
    );
  }

  return (
    <select
      value={currentStr}
      onChange={onChange}
      className="rounded border border-gray-300 px-2 py-1 text-sm"
    >
      <option value="">—</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
      {/* If current value isn't in options and allow_other is False,
          still show it (disabled) so the user can see what's stored. */}
      {currentStr && !options.includes(currentStr) && (
        <option value={currentStr} disabled>
          {currentStr} (not in list)
        </option>
      )}
    </select>
  );
}

// --------------------------------------------------------------------------
// DERIVED value display (masterplan/61 §2.1/2.2; item 12 set 1)
// --------------------------------------------------------------------------

/**
 * A DerivedValue's cell: its value with the DERIVED badge and the
 * inspector popover. Read-only everywhere — the engine writes it. The
 * expression and attribute are on every project page already (the DAG
 * include carries `derived_values: {attribute, expression}`), read from the
 * relationship index like `getLinkedAttributeId`.
 */
export function DerivedValueCell({ dvId }: { dvId: string }) {
  const node = useReactiveNode<any>(dvId);
  const attributeId = firstTargetId(dvId, "attribute");
  const expression = useReactiveNode<any>(firstTargetId(dvId, "expression"));
  return (
    <DerivedValueDisplay
      node={node ?? null}
      derivedBy={expression}
      unitSymbol={getUnitSymbol(attributeId)}
    />
  );
}

/** The name of a DerivedValue's Attribute, for list labels. */
export function derivedValueAttributeName(dvId: string): string {
  const attribute = graphStore.getTargetNodes<any>(dvId, "attribute")[0];
  return (attribute?.name as string) || (attribute?.slug as string) || "(unnamed)";
}

function DerivedValueDisplay({
  node,
  derivedBy,
  unitSymbol,
}: {
  node: any;
  derivedBy: any;
  unitSymbol: string | null;
}) {
  const [open, setOpen] = useState(false);
  const active = useActiveBranch();
  const requestId = node?.calculation_request_id;
  const deliveryKey = JSON.stringify([node?.id, requestId, active?.islandId, active?.branch]);
  const [delivery, setDelivery] = useState<{key: string; result: any} | null>(null);
  useEffect(() => {
    if (!requestId || !["calculating", "timeout"].includes(node?.calculation_status)) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const result = await versioningClient.calculationRead(requestId);
        if (cancelled) return;
        setDelivery({key: deliveryKey, result});
        if (["calculating", "timeout"].includes(result.calculation_status)) timer = setTimeout(poll, 500);
      } catch {
        if (!cancelled) setDelivery({key: deliveryKey, result: {value: null, calculation_status: "unavailable"}});
      }
    };
    void poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [requestId, deliveryKey, node?.calculation_status]);
  // A selected result belongs only to this cell/context. Never put it into
  // the global foreign-node cache, where another page may select differently.
  const shown = delivery?.key === deliveryKey && ["calculating", "timeout"].includes(node?.calculation_status)
    ? {...node, ...delivery.result} : node;
  const value = shown?.value;
  // stale on the server, or about to be: a save in flight changes an input
  const pending = usePendingCalculation(derivedBy?.id);
  // Coherent value status wins over transitional producer metadata. A local
  // save still masks it until the server acknowledges the new calculation.
  const calculationStatus = pending ? "calculating" : shown?.calculation_status ??
    (derivedBy?.is_stale === false ? (derivedBy?.evaluation_error ? "error" : "fresh") : "calculating");
  const isStale = calculationStatus === "calculating" || calculationStatus === "timeout";
  const unavailable = calculationStatus === "unavailable";
  // V33 revised (VC-D 9c61fb1c88): it reads through a conflict only noted in
  // this draft — "incomplete", never a guessed number; the server's sentence
  // names the conflict
  const incomplete = calculationStatus === NOTED_WIRE.calculationStatus;
  const rawError = calculationStatus === "error"
    ? shown?.calculation_error ?? derivedBy?.evaluation_error ?? "Calculation failed."
    : null;
  const evalError: string | null = rawError == null ? null : typeof rawError === "string"
    ? rawError : rawError?.message ?? JSON.stringify(rawError);
  const statusMessage = shown?.calculation_message;

  // Input entities (mentions) for the inspector — read fresh from the
  // relationship index, same pattern as getLinkedAttributeId.
  const mentionNames: string[] = React.useMemo(() => {
    const deId = derivedBy?.id;
    if (!deId) return [];
    try {
      return graphStore
        .getTargetNodes<any>(deId, "mentions")
        .map((m: any) => m?.name)
        .filter(Boolean);
    } catch {
      return [];
    }
  }, [derivedBy]);

  const badgeTone = evalError
    ? "bg-red-50 text-red-600"
    : isStale || incomplete
      ? "bg-amber-50 text-amber-600"
      : "bg-indigo-50 text-indigo-600";

  return (
    <div className="relative inline-flex items-center gap-1">
      <span className="min-w-0">
        {evalError ? (
          <span className="text-red-600">Calculation error</span>
        ) : incomplete ? (
          <span role="status" className="text-amber-700" data-testid="calculation-incomplete"
            title={statusMessage || "Incomplete, because of a conflict this draft has only noted."}>Incomplete</span>
        ) : unavailable ? (
          <span role="status" title={statusMessage || "The selected calculation inputs are unavailable."}>Calculation unavailable</span>
        ) : isStale ? (
          <span role="status" aria-label={calculationStatus === "timeout" ? "Still calculating" : "Calculating value"} title={statusMessage || undefined} className="inline-flex items-center gap-1 text-gray-400">
            <span aria-hidden="true" className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            <span>{calculationStatus === "timeout" ? "Still calculating" : "Calculating…"}</span>
          </span>
        ) : value == null ? (
          <span title="No value" aria-label="No value">—</span>
        ) : (
          <>
            {formatMetricValue(value)}
            {unitSymbol && (
              <span className="ml-1 text-xs text-gray-500">{unitSymbol}</span>
            )}
          </>
        )}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        title={
          evalError
            ? `Computation error: ${evalError}`
            : isStale
              ? "Computed value — inputs changed, re-evaluation pending"
              : "Computed value — click to inspect"
        }
        className={`ml-1 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider cursor-pointer ${badgeTone}`}
      >
        {evalError ? "derived ⚠" : isStale ? "derived ⏳" : incomplete ? "derived ⚠" : "derived"}
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 w-72 rounded border border-gray-200 bg-white p-3 text-left shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
              Derived expression
            </span>
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={() => setOpen(false)}
            >
              ✕
            </button>
          </div>
          {derivedBy?.name && (
            <div className="mb-1 text-xs font-medium text-gray-700">
              {derivedBy.name}
            </div>
          )}
          <code className="block whitespace-pre-wrap break-words rounded bg-gray-50 px-2 py-1 font-mono text-[11px] text-gray-800">
            {derivedBy?.expression ?? "(expression not loaded)"}
          </code>
          {mentionNames.length > 0 && (
            <div className="mt-1.5 text-[11px] text-gray-500">
              Inputs: {mentionNames.join(", ")}
            </div>
          )}
          {evalError && (
            <div className="mt-1.5 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">
              {evalError}
            </div>
          )}
          {!evalError && incomplete && (
            <div className="mt-1.5 text-[11px] text-amber-700" data-testid="calculation-incomplete-reason">
              {statusMessage || "Incomplete, because of a conflict this draft has only noted. Align it, or keep it as an override, and this reads again."}
            </div>
          )}
          {!evalError && (isStale || unavailable) && (
            <div className="mt-1.5 text-[11px] text-amber-600">
              {statusMessage || (unavailable ? "Calculation inputs are unavailable." : calculationStatus === "timeout" ? "Still calculating. The calculation continues." : "Inputs changed — re-evaluation pending.")}
            </div>
          )}
          <CalculationStateView holder={derivedBy?._island ?? node?._island}
            expression={derivedBy?.id} stale={calculationStatus !== "fresh" && !incomplete}
            calculationStatus={calculationStatus} epoch={derivedBy?.dirty_epoch} />
          <div className="mt-1.5 text-[10px] text-gray-400">
            Computed — edits are blocked; change the expression or its
            inputs instead.
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function firstTargetId(id: string | null, rel: string): string | null {
  if (!id) return null;
  try {
    return (graphStore.getTargetNodes<any>(id, rel)[0] as any)?.id ?? null;
  } catch {
    return null;
  }
}

/** The unit an AttributeValue is in: its own `unit_override` when it has
 *  one, else its Attribute's canonical unit. (The editor showed the
 *  canonical unit always, so a value overridden to, say, g read as kg.) */
export function attributeValueUnitSymbol(avId: string | null, attributeId: string | null): string | null {
  if (avId) {
    try {
      const override = graphStore.getTargetNodes<any>(avId, "unit_override")[0] as any;
      if (override?.symbol) return override.symbol;
    } catch {
      /* no override held: the attribute's unit */
    }
  }
  return getUnitSymbol(attributeId);
}

function getUnitSymbol(attributeId: string | null): string | null {
  // canonical_unit lives in GraphStore (DAG-sourced). Same reason we
  // resolve the attribute via the relationship index rather than the
  // attribute node's eager-load slot.
  if (!attributeId) return null;
  try {
    const unitTargets = graphStore.getTargetNodes<any>(
      attributeId,
      "canonical_unit",
    );
    return (unitTargets[0] as any)?.symbol ?? null;
  } catch {
    return null;
  }
}
